/**
 * Reservations. What is actually on the books. Phase 4, plan 21.
 *
 * Read-only, because Guesty is the system of record for a booking (CLAUDE.md §12) and write-back
 * is Phase 9. An edit control here would be a lie: whatever it changed would be overwritten by the
 * next sync, and the owner would learn not to trust the screen.
 *
 * **No guest appears on this screen.** Not the name, not the email, not the phone — the API does
 * not send them (SECURITY.md §5), and the screen does not want them: the question a person brings
 * here is which house, which nights, which channel, what it earned. Who is staying is a Guesty
 * question and belongs where the messaging lives.
 *
 * Every money figure goes through `formatMoney` and nothing else. The API sends integer cents as
 * strings and the browser never does arithmetic on them (CLAUDE.md §3.1).
 */

import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api, type Channel, type Property, type ReservationsResponse } from '../api.js';
import { formatMoney } from '../money.js';

const PAGE_SIZE = 50;

/**
 * The chip a reservation's status gets.
 *
 * The status is shown as the database stores it, never relabelled: `counts_as_booked` is a
 * GENERATED column on `core.reservation` precisely so that one definition of a real stay exists,
 * and a screen that invented friendlier words for it would be a second one.
 */
const STATUS_CHIP: Record<string, string> = {
  confirmed: 'chip chip-ok',
  checked_in: 'chip chip-ok',
  checked_out: 'chip',
  cancelled: 'chip chip-attention',
  declined: 'chip chip-attention',
  expired: 'chip chip-warn',
  inquiry: 'chip chip-warn',
};

export function ReservationsPage(): ReactNode {
  // `?property=` so a link from the Properties screen lands here already filtered, and so the
  // filtered view is a URL a person can keep. Read once as the initial value rather than held in
  // the URL: the picker below is the control, and writing every keystroke back to history would
  // fill the back button with filter states nobody chose to navigate to.
  const [search] = useSearchParams();
  const [propertyId, setPropertyId] = useState(search.get('property') ?? '');
  const [channel, setChannel] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [offset, setOffset] = useState(0);

  const properties = useQuery({
    queryKey: ['properties'],
    queryFn: () => api.get<{ properties: Property[] }>('/api/properties'),
  });
  const channels = useQuery({
    queryKey: ['channels'],
    queryFn: () => api.get<{ channels: Channel[] }>('/api/channels'),
  });

  const query = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
  if (propertyId) query.set('propertyId', propertyId);
  if (channel) query.set('channel', channel);
  if (status) query.set('status', status);
  if (from) query.set('from', from);
  if (to) query.set('to', to);

  const reservations = useQuery({
    queryKey: ['reservations', query.toString()],
    queryFn: () => api.get<ReservationsResponse>(`/api/reservations?${query.toString()}`),
    // The previous page stays on screen while the next one loads, so paging does not blank the
    // table and bounce the scroll position back to the top.
    placeholderData: keepPreviousData,
  });

  /** Any filter change returns to the first page: page 4 of a different filter means nothing. */
  function onFilter<T>(set: (value: T) => void): (value: T) => void {
    return (value) => {
      set(value);
      setOffset(0);
    };
  }

  const data = reservations.data;
  const total = data?.total ?? 0;
  const shown = data?.reservations.length ?? 0;

  return (
    <div className="page">
      <header className="page-header">
        <h1>Reservations</h1>
        <p className="muted">
          What is on the books, from Guesty. Read-only here — Guesty is the system of record for a
          booking, and nothing on this screen writes to it.
        </p>
      </header>

      <section className="card filters">
        <label>
          Property
          <select value={propertyId} onChange={(e) => onFilter(setPropertyId)(e.target.value)}>
            <option value="">All properties</option>
            {(properties.data?.properties ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>

        <label>
          Channel
          <select value={channel} onChange={(e) => onFilter(setChannel)(e.target.value)}>
            <option value="">All channels</option>
            {(channels.data?.channels ?? []).map((c) => (
              <option key={c.key} value={c.key}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <label>
          Status
          <select value={status} onChange={(e) => onFilter(setStatus)(e.target.value)}>
            <option value="">Any status</option>
            {Object.keys(STATUS_CHIP).map((key) => (
              <option key={key} value={key}>
                {key.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </label>

        <label>
          Staying on or after
          <input type="date" value={from} onChange={(e) => onFilter(setFrom)(e.target.value)} />
        </label>

        <label>
          Staying before
          <input type="date" value={to} onChange={(e) => onFilter(setTo)(e.target.value)} />
        </label>
      </section>

      {reservations.isError && <div className="alert">{reservations.error.message}</div>}

      <section className="card">
        <div className="table-header">
          <span className="muted">
            {reservations.isLoading
              ? 'Loading…'
              : total === 0
                ? 'No reservations match these filters.'
                : `${offset + 1}–${offset + shown} of ${total}`}
          </span>
        </div>

        <table className="table">
          <thead>
            <tr>
              <th>Property</th>
              <th>Stay</th>
              <th className="num">Nights</th>
              <th>Status</th>
              <th>Channel</th>
              <th>Booked</th>
              <th className="num">Accommodation</th>
              <th className="num">Cleaning</th>
              <th className="num">Taxes</th>
              <th className="num">Host payout</th>
              <th className="num">Total paid</th>
            </tr>
          </thead>
          <tbody>
            {(data?.reservations ?? []).map((r) => (
              <tr key={r.id}>
                <td>
                  {r.propertyName}
                  {r.confirmationCode && <div className="muted small">{r.confirmationCode}</div>}
                </td>
                <td>
                  {r.checkIn} → {r.checkOut}
                </td>
                <td className="num">{r.nights}</td>
                <td>
                  <span className={STATUS_CHIP[r.status] ?? 'chip'}>
                    {r.status.replaceAll('_', ' ')}
                  </span>
                  {/* Whether it counts as a real stay is the database's answer, not the screen's. */}
                  {!r.countsAsBooked && (
                    <div className="muted small" title="core.reservation.counts_as_booked is false">
                      not counted as booked
                    </div>
                  )}
                </td>
                <td>{r.channel}</td>
                <td>
                  {r.bookedAt ? (
                    r.bookedAt.slice(0, 10)
                  ) : (
                    <span className="muted" title="Guesty sent no createdAt for this booking">
                      —
                    </span>
                  )}
                </td>
                <td className="num">{formatMoney(r.accommodation)}</td>
                <td className="num">{formatMoney(r.cleaningFee)}</td>
                <td className="num">{formatMoney(r.taxes)}</td>
                <td className="num">{formatMoney(r.hostPayout)}</td>
                <td className="num">{formatMoney(r.totalPaid)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="pager">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => setOffset(offset - PAGE_SIZE)}
          >
            Previous
          </button>
          <button
            type="button"
            disabled={offset + shown >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
          </button>
        </div>
      </section>
    </div>
  );
}
